import { dictionaryApi } from './dictionaryClient';
import {
  SUBANTA_BACHAN,
  SUBANTA_BARGA,
  SUBANTA_LINGA,
  SUBANTA_PRAKAR,
  SUBANTA_SHRENI,
  SUBANTA_VYUTPANNA,
} from '../constants/subantaEnums';
import type { Artha } from './arthaApi';
import type { DictionaryNotification } from './notificationApi';
import type { DictionaryStat } from './statsApi';

export enum Vedic {
  LAUKIK = 'laukik',
  SAMAS = 'samas',
  AYATIT = 'ayatit',
  NIRMIT = 'nirmit',
}

export type SubantaLinga = (typeof SUBANTA_LINGA)[number];
export type SubantaVyutpanna = (typeof SUBANTA_VYUTPANNA)[number];
export type SubantaPrakar = (typeof SUBANTA_PRAKAR)[number];
export type SubantaShreni = (typeof SUBANTA_SHRENI)[number];
export type SubantaBarga = (typeof SUBANTA_BARGA)[number];
export type SubantaBachan = (typeof SUBANTA_BACHAN)[number];

/** Scalar fields stored on Subanta (API shape). */
export type SubantaScalars = {
  word: string;
  nepali_order?: number | null;
  hindi_order?: number | null;
  base_word: string;
  iast: string;
  bat: string | null;
  linga: SubantaLinga;
  vyutpanna: SubantaVyutpanna;
  prakar: SubantaPrakar;
  shreni: SubantaShreni;
  shatwa: boolean;
  natwa: boolean;
  vedic: Vedic;
  barga: SubantaBarga;
  bachan: SubantaBachan;
};

/** One-to-many relations (TypeORM), included on list/detail when API loads them. */
export type SubantaRelations = {
  vyutpattis?: unknown[];
  nispattis?: unknown[];
  arthas?: Artha[];
  samastaWords?: unknown[];
  samastas?: unknown[];
  rups?: unknown[];
  /** At most one row per subanta; API may return object or single-element array. */
  media?: import('./mediaApi').DictionaryMedia | import('./mediaApi').DictionaryMedia[] | unknown[] | unknown;
  stats?: DictionaryStat[] | unknown[];
  notifications?: DictionaryNotification[] | unknown[];
};

/** API row: scalars + id + timestamps + relations. */
export type Subanta = SubantaScalars & {
  id: number;
  createdAt?: string;
  updatedAt?: string;
  /** Some serializers may expose DB column names. */
  created_at?: string;
  updated_at?: string;
} & SubantaRelations;

export type CreateSubantaBody = SubantaScalars;
export type PaginatedSubantaResult = {
  data: Subanta[];
  total: number;
  page: number;
  limit: number;
};

function normalizeList(data: unknown): Subanta[] {
  if (Array.isArray(data)) return data as Subanta[];
  if (data && typeof data === 'object' && Array.isArray((data as { data?: unknown }).data)) {
    return (data as { data: Subanta[] }).data;
  }
  return [];
}

export type SubantaSearchMode = 'similar' | 'exact';

export type SubantaSearchParams = {
  word: string;
  mode?: SubantaSearchMode;
  limit?: number;
  linga?: SubantaLinga;
};

export async function searchSubantas(params: SubantaSearchParams): Promise<Subanta[]> {
  const word = params.word.trim();
  if (!word) return [];
  const q: Record<string, string | number> = { word };
  if (params.mode) q.mode = params.mode;
  if (params.limit != null && Number.isFinite(params.limit)) q.limit = Math.floor(params.limit);
  if (params.linga) q.linga = params.linga;
  const res = await dictionaryApi.get<unknown>('/subanta/search', { params: q });
  return normalizeList(res.data);
}

export async function listSubantas(): Promise<Subanta[]> {
  const res = await dictionaryApi.get<unknown>('/subanta');
  return normalizeList(res.data);
}

export async function listSubantasPaginated(page: number, limit: number): Promise<PaginatedSubantaResult> {
  const safePage = Number.isFinite(page) && page > 0 ? Math.floor(page) : 1;
  const safeLimit = Number.isFinite(limit) && limit > 0 ? Math.floor(limit) : 20;
  const res = await dictionaryApi.get<unknown>('/subanta/paginated', {
    params: { page: safePage, limit: safeLimit },
  });
  const payload = res.data as
    | {
        data?: unknown;
        total?: unknown;
        page?: unknown;
        limit?: unknown;
        pagination?: {
          total?: unknown;
          page?: unknown;
          limit?: unknown;
        };
      }
    | unknown[];
  if (Array.isArray(payload)) {
    return { data: payload as Subanta[], total: payload.length, page: safePage, limit: safeLimit };
  }
  const rows = normalizeList(payload);
  const totalRaw =
    payload && typeof payload === 'object'
      ? (payload as { total?: unknown; pagination?: { total?: unknown } }).total ??
        (payload as { pagination?: { total?: unknown } }).pagination?.total
      : undefined;
  const pageRaw =
    payload && typeof payload === 'object'
      ? (payload as { page?: unknown; pagination?: { page?: unknown } }).page ??
        (payload as { pagination?: { page?: unknown } }).pagination?.page
      : undefined;
  const limitRaw =
    payload && typeof payload === 'object'
      ? (payload as { limit?: unknown; pagination?: { limit?: unknown } }).limit ??
        (payload as { pagination?: { limit?: unknown } }).pagination?.limit
      : undefined;
  const total = typeof totalRaw === 'number' ? totalRaw : Number(totalRaw);
  const nextPage = typeof pageRaw === 'number' ? pageRaw : Number(pageRaw);
  const nextLimit = typeof limitRaw === 'number' ? limitRaw : Number(limitRaw);
  return {
    data: rows,
    total: Number.isFinite(total) && total >= 0 ? total : rows.length,
    page: Number.isFinite(nextPage) && nextPage > 0 ? Math.floor(nextPage) : safePage,
    limit: Number.isFinite(nextLimit) && nextLimit > 0 ? Math.floor(nextLimit) : safeLimit,
  };
}

export async function getSubanta(id: number): Promise<Subanta> {
  const res = await dictionaryApi.get<Subanta>(`/subanta/${id}`);
  return res.data;
}

export async function createSubanta(body: CreateSubantaBody): Promise<Subanta> {
  const res = await dictionaryApi.post<Subanta>('/subanta', body);
  return res.data;
}

export async function updateSubanta(
  id: number,
  body: Partial<SubantaScalars>
): Promise<Subanta> {
  const res = await dictionaryApi.patch<Subanta>(`/subanta/${id}`, body);
  return res.data;
}

export async function deleteSubanta(id: number): Promise<void> {
  await dictionaryApi.delete(`/subanta/${id}`);
}

export async function mergeSubantas(srcId: number, destinationId: number): Promise<void> {
  await dictionaryApi.post('/subanta/merge', {
    srcId,
    destinationId,
  });
}
